import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import AwsS3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import useUppyState from '@uppy/react/lib/useUppyState';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { SSE } from 'sse.js';
import { v4 as uuidv4 } from 'uuid';

import { FileDuplicatesInfo } from './FileDuplicatesInfo';
import { FileExplorer } from './FileExplorer';
import { GenericUppyFile, getUppyAWSS3Options, UploadType } from './uppy';
import { ViewToggle } from './ViewToggle';
import { View } from './ViewToggle';
import { allowedFileTypes } from '../../../../../backend/src/case/doc/allowed-extensions';
import { OrgPermission } from '../../../../../backend/src/organisation/organisation-permission';
import { UploadFailureReason } from '../../../../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useEmailAttachmentWarnings } from '@/api/hooks/useEmailAttachmentWarnings';
import { APIBaseChronos } from '@/api/hosts';
import { useDeleteCaseFiles } from '@/api/mutations/useDeleteAllCaseFiles';
import { useRemoveFiles } from '@/api/mutations/useRemoveFiles';
import { useSendPendingFailures, PendingFailure } from '@/api/mutations/useSendPendingFailures';
import { useGetCaseIsPendingUpload } from '@/api/queries/useGetCaseIsPendingUpload';
import useGetFiles from '@/api/queries/useGetFiles';
import { ActiveUploadsProgress } from '@/components/organisms/ActiveUploadsProgress';
import FileUploadInput from '@/components/organisms/FileUploadInput';
import { useUserContext } from '@/Contexts/User';
import { ActiveJob } from '@/hooks/useJobProgress';
import { Icon } from '@/new/components/icon';

const FileUploaderComponent = ({
  setUploadDisabled,
  setTotalUnprocessedDocs,
  caseId,
  isCaseCreator = false,
  activeJobs = [],
}: {
  setUploadDisabled: (disabled: boolean) => void;
  setTotalUnprocessedDocs: (n: number) => void;
  caseId: string;
  isCaseCreator?: boolean;
  activeJobs?: ActiveJob[];
}) => {
  const authInfo = useAuthInfo();
  const { user } = useUserContext();

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [fileType, setFileType] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());

  const [checkingForDuplicates, setCheckingForDuplicates] = useState(false);
  const [duplicateScanSuccess, setDuplicateScanSuccess] = useState(false);
  const [duplicateNumber, setDuplicateNumber] = useState(0);

  const uploadIdRef = useRef<string | null>(null);
  const pendingFailuresRef = useRef<Array<PendingFailure>>([]);
  const uploadContainsZipRef = useRef<boolean>(false);

  const [zipIsProcessing, setZipIsProcessing] = useState(false);

  const {
    data: filesResponse,
    refetch: refetchFiles,
    isLoading: isLoadingFiles,
  } = useGetFiles(caseId, page, searchQuery, fileType);
  const [view, setView] = useState<View>((filesResponse?.files?.length ?? 0 > 0) ? View.Explorer : View.Upload);

  const { refetch: caseIsPendingUploadRefetch } = useGetCaseIsPendingUpload(caseId);
  const { mutateAsync: deleteAllFiles, isLoading: isDeletingAllFiles } = useDeleteCaseFiles();
  const { mutateAsync: sendPendingFailures } = useSendPendingFailures();

  // Email attachment warnings - show toast notifications for unsupported email attachments
  useEmailAttachmentWarnings({
    caseId,
    enabled: !!caseId,
  });

  const onApideckSuccess = useCallback(() => {
    caseIsPendingUploadRefetch();
    refetchFiles();
    setView(View.Explorer);
  }, [caseIsPendingUploadRefetch, refetchFiles]);

  const [uppy] = useState(() =>
    new Uppy({
      restrictions: {
        maxNumberOfFiles: 10000,
        allowedFileTypes,
      },

      allowMultipleUploadBatches: true,

      onBeforeFileAdded: (file) => {
        if (file.name === undefined) {
          return;
        }

        const id = uuidv4();
        const extension = file.name.split('.').length > 1 ? file.name.split('.').pop() : undefined;
        const extensionLowerCase = extension?.toLowerCase();
        const name = `${id}${extensionLowerCase ? `.${extensionLowerCase}` : ''}`;

        fileIdNameMap.set(id, file.name);

        // Make a new File with the correct name as S3 only cares about the File contents' name
        const newFile = new File([file.data], name, { type: file.data.type });

        return { ...file, data: newFile, name, meta: { ...file.meta, name } };
      },
    }).use(AwsS3, getUppyAWSS3Options(getAccessToken, caseId, UploadType.Raw)),
  );

  const [isUploading, setIsUploading] = useState(false);

  const fileIdNameMap = useMemo(() => new Map<string, string>(), []);
  const completedUploads = useMemo(() => new Set<string>(), []);
  const [completedUploadsCount, setCompletedUploadsCount] = useState(0);

  const completedUploadsInBatch: UppyFile<{ id?: string }, {}>[] = useMemo(() => [], []);
  const recordUploadedFileToDB = useMemo(
    () => new Worker(new URL('../../../workers/record-doc-upload.ts', import.meta.url), { type: 'module' }),
    [],
  );

  const handleFileSelect = (fileId: string) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = () => {
    removeFiles(Array.from(selectedFiles));
    setSelectedFiles(new Set());
  };

  async function getAccessToken() {
    return await authInfo.tokens.getAccessToken();
  }

  async function setupSSEListener(): Promise<void> {
    const accessToken = await getAccessToken();
    const sseResponse = new SSE(`${APIBaseChronos}/api/files/${caseId}/duplicate-scan/${uploadIdRef.current}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const transitionToExplorer = () => {
      setView(View.Explorer);
      setCheckingForDuplicates(false);
      setDuplicateScanSuccess(false);
      uploadIdRef.current = null;
      sseResponse.close();
    };

    sseResponse.onmessage = async (msg: { data: string }) => {
      const parsed: { duplicateNumber: number } = JSON.parse(msg.data);

      refetchFiles();
      setDuplicateNumber(parsed.duplicateNumber);
      setDuplicateScanSuccess(true);

      setTimeout(() => {
        transitionToExplorer();
      }, 2000);
    };

    sseResponse.onerror = () => {
      refetchFiles();
      setDuplicateScanSuccess(true);

      setTimeout(() => {
        transitionToExplorer();
      }, 2000);
    };
  }

  const sendPendingFailuresCallback = useCallback(async () => {
    if (pendingFailuresRef.current.length > 0 && uploadIdRef.current) {
      await sendPendingFailures({
        caseId,
        uploadId: uploadIdRef.current,
        failures: pendingFailuresRef.current,
      });

      // Clear pending failures after sending
      pendingFailuresRef.current = [];
    }
  }, [caseId, sendPendingFailures]);

  const requestWorkerUpdateFileDatabase = useCallback(
    async (isFinal = false) => {
      if (window.Worker) {
        const token = await getAccessToken();
        recordUploadedFileToDB.postMessage({
          accessToken: token,
          caseId,
          files: completedUploadsInBatch.map((file) => {
            const id = file.name?.split('.')[0] ?? '';

            return {
              id,
              name: fileIdNameMap.get(id),
              size: file.size,
              extension: file.extension,
            };
          }),
          isFinal,
          uploadId: uploadIdRef.current,
        });

        completedUploadsInBatch.splice(0, completedUploadsInBatch.length);
      }
    },
    [caseId, fileIdNameMap, completedUploadsInBatch, recordUploadedFileToDB],
  );

  const handleUppyUploadSuccess = useCallback(
    (file: UppyFile<{ id?: string }, {}> | undefined) => {
      if (file && !completedUploads.has(file.id)) {
        completedUploads.add(file.id);
        completedUploadsInBatch.push(file);

        // Track if any uploaded file is a zip
        if (file.extension === 'zip') {
          uploadContainsZipRef.current = true;
        }

        setCompletedUploadsCount(completedUploads.size);

        // Send every 10 completed docs to the file creation endpoint on the backend
        // which uses a web worker to keep the main thread focused on uploading
        if (completedUploadsInBatch.length > 10) {
          requestWorkerUpdateFileDatabase();
        }
      }
    },
    [completedUploads, completedUploadsInBatch, requestWorkerUpdateFileDatabase],
  );

  const handleUppyUploadComplete = useCallback(async () => {
    await requestWorkerUpdateFileDatabase(true);
    // Wait for worker to complete
    await new Promise((resolve) => (recordUploadedFileToDB.onmessage = resolve));

    await sendPendingFailuresCallback();

    uppy.resetProgress();
    uppy.removeFiles([...completedUploads].map((id) => id));

    setCompletedUploadsCount(0);
    completedUploads.clear();
    fileIdNameMap.clear();

    setCompletedUploadsCount(0);
    setIsUploading(false);

    // Only start duplicate scan for non-zip uploads
    // For zip uploads, the useEffect monitoring activeJobs will handle it after extraction
    if (!uploadContainsZipRef.current) {
      setCheckingForDuplicates(true);
      setupSSEListener();
    }
    // Reset the flag for next upload
    uploadContainsZipRef.current = false;
  }, [
    uppy,
    fileIdNameMap,
    completedUploads,
    requestWorkerUpdateFileDatabase,
    recordUploadedFileToDB,
    sendPendingFailuresCallback,
  ]);

  const handleUppyRestrictionFailed = useCallback((file: GenericUppyFile | undefined, error: Error) => {
    if (file?.id || file?.name) {
      const id = file.name?.split('.')[0] ?? '';
      const extension = file.name?.split('.').pop() || '';
      const filename = fileIdNameMap.get(id);

      pendingFailuresRef.current.push({
        filename: filename || '',
        extension,
        rejectedReason: UploadFailureReason.UnsupportedFileFormat,
      });
    }

    toast.error(error.message);
  }, []);

  useEffect(() => {
    uppy.off('upload-success', handleUppyUploadSuccess);
    uppy.off('complete', handleUppyUploadComplete);
    uppy.off('restriction-failed', handleUppyRestrictionFailed);

    uppy.on('upload-success', handleUppyUploadSuccess);
    uppy.on('complete', handleUppyUploadComplete);
    uppy.on('restriction-failed', handleUppyRestrictionFailed);
  }, [
    handleUppyUploadComplete,
    handleUppyUploadSuccess,
    requestWorkerUpdateFileDatabase,
    handleUppyRestrictionFailed,
    uppy,
  ]);

  const { mutate: removeFiles, isLoading: isRemovingFiles } = useRemoveFiles({ caseId });

  const onUploadUppy = useCallback(
    (files: File[]) => {
      uppy.addFiles(files.map((file) => ({ source: 'local', data: file, name: file.name })));
      uppy.upload();

      setIsUploading(true);

      // Create an id for the current upload
      uploadIdRef.current = uuidv4();
      // Don't set checkingForDuplicates here - wait for upload to complete
      // For regular files: handleUppyUploadComplete will trigger duplicate scan
      // For zips: useEffect monitoring activeJobs will trigger duplicate scan after extraction
    },
    [uppy],
  );

  const totalProgress = useUppyState(uppy, (state) => state.totalProgress);

  useEffect(() => {
    if (filesResponse) {
      setTotalUnprocessedDocs(filesResponse.totalCount);
    }
  }, [filesResponse, setTotalUnprocessedDocs]);

  useEffect(() => {
    setUploadDisabled(isUploading || (filesResponse?.files?.length || 0) === 0);
  }, [filesResponse, isUploading, setUploadDisabled]);

  // Track if we had an active zip job that just completed
  const previousActiveJobsRef = useRef<ActiveJob[]>([]);

  useEffect(() => {
    const hasActiveJobs = activeJobs && activeJobs.length > 0;
    const hadActiveJobs = previousActiveJobsRef.current.length > 0;

    // Update zipIsProcessing based on whether we have active jobs
    if (hasActiveJobs) {
      setZipIsProcessing(true);
    } else if (!hasActiveJobs && hadActiveJobs && uploadIdRef.current && !checkingForDuplicates) {
      // Zip job just completed (went from having jobs to no jobs)
      // Now start the duplicate scan
      setZipIsProcessing(false);
      setCheckingForDuplicates(true);
      setupSSEListener();
    }

    // Store current activeJobs for next render
    previousActiveJobsRef.current = activeJobs || [];
  }, [activeJobs, checkingForDuplicates]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden">
      <div className="mb-2 flex items-center justify-between gap-2">
        <ViewToggle view={view} setView={setView} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          className="flex h-full min-h-0 flex-col"
          key={view}
          initial={{ x: view === 'upload' ? 100 : -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: view === 'upload' ? 100 : -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 40 }}
        >
          {view === 'upload' ? (
            <>
              <FileUploadInput
                isUploading={isUploading}
                onDrop={onUploadUppy}
                totalProgress={totalProgress}
                successfulUploads={completedUploadsCount}
                caseId={caseId}
                onApideckSuccess={onApideckSuccess}
                isApideckEnabled={user?.organisation?.permissions.includes(OrgPermission.Apideck)}
                uploadIdRef={uploadIdRef}
                activeJobs={activeJobs}
              />

              <div className="mt-4 flex gap-3 rounded-lg border border-blue-200 bg-blue-50/80 p-4 text-sm text-blue-700">
                <Icon className="lucide--info pt-5.5" />
                <div>
                  <p className="mb-1 text-base text-blue-800">Uploading a large number of files?</p>
                  <p>
                    Uploading too many (&gt;1000) files directly may cause performance issues or browser crashes.
                    Compress files into a ZIP archive first, and upload that for better reliability.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-col text-xs text-gray-500">
                <p>
                  We automatically check for identical files, by scanning the file content, and remove them from the
                  upload.
                </p>
              </div>
            </>
          ) : (
            <FileExplorer
              isLoadingFiles={isLoadingFiles || isRemovingFiles}
              allFiles={filesResponse?.files ?? []}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              setFileType={setFileType}
              fileType={fileType}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleFileSelect={handleFileSelect}
              handleBulkDelete={handleBulkDelete}
              deleteAllFiles={async () => {
                setUploadDisabled(true);
                await deleteAllFiles({ caseId });
                setUploadDisabled(false);
                trackCustomEvent(TrackingEvents.DeleteAllFiles, { caseId });
              }}
              isDeletingAllFiles={isDeletingAllFiles}
              page={page ?? 1}
              setPage={setPage}
              totalPages={filesResponse?.pageCount ?? 1}
              isCaseCreator={isCaseCreator}
            />
          )}

          <div className="mt-auto text-gray-700">
            {checkingForDuplicates ? (
              <FileDuplicatesInfo duplicateScanSuccess={duplicateScanSuccess} duplicateNumber={duplicateNumber} />
            ) : (
              <ActiveUploadsProgress activeJobs={activeJobs} zipIsProcessing={zipIsProcessing} />
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default FileUploaderComponent;
